import { useCallback, useEffect, useState } from "react";
import { Users, Download, MessageCircle, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Leads({ activeBusiness, businesses, role }) {
  const allBizLabel = role === "super_admin" ? "Semua Bisnis" : "Semua Bisnis Saya";
  const [filter, setFilter] = useState("all");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { business_id: filter } : {};
      const r = await api.get("/leads", { params });
      setLeads(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = filter !== "all" ? `?business_id=${filter}` : "";
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/leads/export${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "leads.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV diunduh");
    } catch (e) {
      toast.error("Gagal mengunduh");
    }
  };

  const waLink = (num) => `https://wa.me/${(num || "").replace(/[^0-9]/g, "").replace(/^0/, "62")}`;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="bg-[#FFFFFF] border-[#E2E8F0] w-full sm:w-[220px]" data-testid="leads-filter-business"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
              <SelectItem value="all">{allBizLabel}</SelectItem>
              {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">{leads.length} lead</span>
        </div>
        <Button onClick={exportCsv} data-testid="btn-export-leads-csv" variant="outline" className="border-[#E2E8F0] bg-[#FFFFFF] hover:bg-[#F1F5F9] rounded-xl">
          <Download size={16} className="mr-1" /> Export CSV
        </Button>
      </div>

      <div data-testid="leads-table-container" className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl min-w-0 overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin mx-auto mb-2" /> Memuat...</div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={32} className="mx-auto text-slate-600 mb-3" />
            <p className="text-slate-600">Belum ada lead. Lead dari chatbot akan muncul di sini.</p>
          </div>
        ) : (
          <table className="w-full min-w-[680px] text-sm">
            <thead>
              <tr className="border-b border-[#E2E8F0] text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-medium">Nama</th>
                <th className="px-5 py-3 font-medium">WhatsApp</th>
                <th className="px-5 py-3 font-medium">Bisnis</th>
                <th className="px-5 py-3 font-medium">Pertanyaan</th>
                <th className="px-5 py-3 font-medium">Tanggal</th>
                <th className="px-5 py-3 font-medium">Notif</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[#F1F5F9] hover:bg-[#F1F5F9]/50 transition-colors">
                  <td className="px-5 py-3 font-medium">{l.name}</td>
                  <td className="px-5 py-3 text-slate-700 font-mono-code text-xs">{l.whatsapp}</td>
                  <td className="px-5 py-3 text-slate-600">{l.business_name}</td>
                  <td className="px-5 py-3 text-slate-600 max-w-full sm:w-[220px] truncate">{l.question || "-"}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{new Date(l.created_at).toLocaleString("id-ID")}</td>
                  <td className="px-5 py-3">
                    {l.notified ? <CheckCircle2 size={16} className="text-[#059669]" /> : <XCircle size={16} className="text-slate-600" />}
                  </td>
                  <td className="px-5 py-3">
                    <a href={waLink(l.whatsapp)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#25D366] hover:underline text-xs font-medium">
                      <MessageCircle size={14} /> Chat
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
